import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { DashboardService } from 'src/app/Services/dashboard.service';
import { ArtistDashboard } from 'src/app/models/response/artistdashboard.models';
import { OnBoardingService } from 'src/app/Services/shared/onboarding.service';
import { ImageUtils } from 'src/app/shared/utils/image-utils';

@Component({
    selector: 'ds-artist-dashboard',
    templateUrl: './ds-artist-dashboard.component.html',
    styleUrls: ['./ds-artist-dashboard.component.css'],
    providers: [DashboardService],
    standalone: false
})
export class DsArtistDashboardComponent implements OnInit {
    public dashboardData: ArtistDashboard = {
        IncomingArtistConnectionRequest: []
    };
    public artistId!: string;
    public firstName: string = '';
    public dashboardDataLoaded: boolean = false;
    public selectedTabIndex: number = 0;
    public isAuthenticated!: boolean;
    public isArtist: boolean = false;
    public showDashboard: boolean = true;
    public authConfig: AuthConfig = {};

    constructor(
        private ngxSpinner: NgxSpinnerService,
        private router: Router,
        private authService: AuthService,
        private dashboardService: DashboardService,
        private onBoardingService: OnBoardingService
    ) { }

    public ngOnInit(): void {
        this.getArtistDashboard();
        this.artistId = this.authService.getUserId();
        const currentUser = this.authService.getCurrentUser();
        this.firstName = currentUser?.FirstName || 'Artist';
        this.isAuthenticated = this.authService.isAuthenticated();
        if (this.isAuthenticated) {
            this.authConfig = this.authService.getAuthConfig();
            this.isArtist = this.authConfig.IsArtist || false;
            this.onBoardingService.getUpdatedLocalStorageValue.subscribe((tourVisible: boolean) => {
                this.showDashboard = !tourVisible;
            });
        }
    }

    public getArtistDashboard() {
        this.ngxSpinner.show();
        this.dashboardService.getArtistDashboard().subscribe((data: ArtistDashboard) => {
            data.BestPerformingSeriesData?.forEach((item) => {
                const colorClass = this.getColorBasedOnPercentage(item.PercentageOfTotal);
                item.ColorClassBasedOnPercentage = 'bg-' + colorClass;
                item.TextBasedOnPercentage = `<span class="text-${colorClass}">${item.PercentageOfTotal}%</span>`;
            });
            this.dashboardData = data;
            if (data.RecentSales) {
                this.dashboardData.RecentSales = data.RecentSales.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.ProductImageUrl || 'assets/images/No-Image.png');
                    return item;
                });
            }
            if (data.NewProductsAdded) {
                this.dashboardData.NewProductsAdded = data.NewProductsAdded.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.ProductImageUrl || 'assets/images/No-Image.png');
                    return item;
                });
            }
            this.ngxSpinner.hide();
            this.dashboardDataLoaded = true;
        }, error => {
            console.error(error);
            this.ngxSpinner.hide();
        });
    }

    private getColorBasedOnPercentage(percentage: number): string {
        if (percentage >= 75) {
            return 'success';
        } else if (percentage >= 50) {
            return 'info';
        } else if (percentage >= 25) {
            return 'warning';
        }
        return 'danger';
    }

    public goToProductDetails(product: any) {
        this.router.navigate(['/artists', 'artworks', product.ProductId]);
    }

    public goToProductRequestPage() {
        this.router.navigate(['/artists', 'product-requests']);
    }

    public onViewConsignmentsClicked(data: any) {
        if (data && data.GalleryId) {
            this.router.navigate(['/artists', 'gallery', data.GalleryId, 'gallery-products']);
        } else {
            console.error('Invalid data object:', data);
        }
    }

    public ChatwithGallery(data: any) {
        if (data && data.Id) {
            this.router.navigate(['/inbox', data.Id]);
        } else {
            console.error('Invalid data object:', data);
        }
    }

    getFirstSentence(message: string, wordCount: number = 10): string {
        if (!message) return '';
        const words = message.split(' ');
        if (words.length <= wordCount) {
            return message;
        }
        return words.slice(0, wordCount).join(' ') + '...';
    }

    public onTabChange(event: any) {
        if (event.index !== undefined) {
            this.selectedTabIndex = event.index;
        }
    }

    public navigateToArtworks() {
        this.router.navigate(['/artists', 'artworks']);
    }

    public navigateToCreateArtwork() {
        this.router.navigate(['/artists', 'artworks', 'create']);
    }

    public navigateToConnectedGalleries() {
        this.router.navigate(['/artists', 'connected-galleries']);
    }

    public navigateToConsignments() {
        this.router.navigate(['/view-consignments']);
    }

    public navigateToEvents() {
        this.router.navigate(['/events', 'my-participated-events']);
    }

    public navigateToPortfolio() {
        this.router.navigate(['/artists', 'portfolio']);
    }

    public navigateToMarketing() {
        this.router.navigate(['/social-media']);
    }

    public sharePortfolio() {
        const portfolioUrl = `https://portal.artflow-pro.com/profile/${this.artistId}`;
        if (navigator.share) {
            navigator.share({
                title: 'My Art Portfolio',
                text: 'Check out my art portfolio!',
                url: portfolioUrl
            });
        } else {
            navigator.clipboard.writeText(portfolioUrl);
        }
    }

    public viewPortfolio() {
        const portfolioUrl = `https://portal.artflow-pro.com/profile/${this.artistId}`;
        window.open(portfolioUrl, '_blank');
    }
}
